import * as apiGet from 'apiAuto/backend/paths/GetShoppingCartV1';
import { Functor } from 'functions/Functor';
import { AppearanceList } from 'types/ShoppingCart/AppearanceList';
import EntityType from 'types/ShoppingCart/EntityType';
import * as ShoppingCart from 'types/ShoppingCart/ShoppingCart';

import STATUS from './Status';

export * as ShoppingCartList from './ShoppingCartList';

export type Type = ShoppingCart.Type[];

export function create(): Type {
	return [];
}

export function remove(list: Type, id: string): Type {
	return list.filter((e) => e.id !== id);
}

export async function setDefault(list: Type, id: string): Promise<Type> {
	await ShoppingCart.setDefaultOnApi(id);
	return list.map((e) => {
		return {
			...e,
			default: e.id === id,
		};
	});
}

export function getDefault(list: Type): ShoppingCart.Type | undefined {
	return list.find((e) => e.default === true);
}

export function hasDefault(list: Type): boolean {
	for (const i of list) {
		if (i.default) return true;
	}
	return false;
}

export function orderByDefault(list: Type): Type {
	return list.sort((a, b) => {
		if (a.default) return -1;
		if (b.default) return 1;
		return 0;
	});
}

export function hasOpenCart(list: Type): boolean {
	for (const i of list) {
		if (i.status === STATUS.LIVE) return true;
	}
	return false;
}

export function getById(list: Type, id: string): ShoppingCart.Type | undefined {
	return list.find((e) => e.id === id);
}

export async function initializeWithoutDetails(list: Type): Promise<Type> {
	const carts = await getFromApi();
	return carts.map((e) => {
		if (!e.default) return e;
		const defaultCart: ShoppingCart.Type | undefined = list.find((e) => e.default);
		if (!defaultCart) return e;
		return {
			...e,
			appearances: defaultCart.appearances,
			products: defaultCart.products,
		};
	});
}

export async function initialize(): Promise<Type> {
	return await Functor.From(await getFromApi())
		.mapAsync(getDetailsOfDefaultShoppingCart)
		.mapAsync(addDefaultCartIfNoDefaultCartExists)
		.get();
}

export async function addDefaultCartIfNoDefaultCartExists(
	list: Type,
	previousDefaultName?: string
): Promise<Type> {
	if (hasDefault(list)) return list;
	if (hasOpenCart(list)) {
		const newDefaultCart = list.find((cart) => cart.status === STATUS.LIVE);
		if (newDefaultCart) return await setDefault(list, newDefaultCart?.id);
	}
	const cart = await ShoppingCart.addNewDefaultCartAndSave(
		previousDefaultName ?? previousDefaultName
	);
	return [cart, ...list];
}

export async function getFromApi(): Promise<Type> {
	return (await apiGet.callApi()).getOrDefault([]).map(ShoppingCart.mapFromApiGetAll);
}

export async function deleteFromApi(list: Type, id: string, name: string): Promise<Type> {
	await ShoppingCart.deleteFromApi(id);
	return Functor.From(id)
		.map((id) => remove(list, id))
		.mapAsync((updatedList) => addDefaultCartIfNoDefaultCartExists(updatedList, name))
		.get();
}

export function getCart(list: Type, id: string | undefined): ShoppingCart.Type | undefined {
	const cart = list.find((e) => e.id === id);
	if (!cart) return getDefault(list);
	return cart;
}

export async function addEntityToCart(
	list: Type,
	entity: {
		id: string;
		basketIds?: string[];
		type: EntityType;
		amount?: number;
	},
	shoppingCartId: string | undefined
): Promise<Type> {
	const cart: ShoppingCart.Type | undefined = getCart(list, shoppingCartId);
	if (!cart) return list;
	const appearances = await ShoppingCart.addEntityToCart(cart, entity);
	return replaceAppearances(list, cart.id, appearances);
}

export function replaceAppearances(
	list: Type,
	shoppingCartId: string | undefined,
	appearances: AppearanceList.Type
): Type {
	return list.map((e) => {
		if (!(e.id === shoppingCartId)) return e;
		return { ...e, appearances: appearances };
	});
}

export async function moveToNextStatus(
	list: Type,
	id: string,
	previousDefaultName: string
): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.moveToNextStatus(i));
	}
	return await addDefaultCartIfNoDefaultCartExists(output, previousDefaultName);
}

export async function moveToPrevStatus(list: Type, id: string): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.moveToPrevStatus(i));
	}
	return output;
}

export async function getDetailsOfDefaultShoppingCart(list: Type): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (!i.default) output.push(i);
		else output.push(await ShoppingCart.initialize(i));
	}
	return output;
}

export async function enrichWithProductInformations(list: Type, id: string): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.enrichWithProductInformations(i));
	}
	return output;
}

export async function getDetails(list: Type, id: string): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.initialize(i));
	}
	return output;
}

export function setName(list: Type, id: string, name: string): Type {
	return list.map((e) => {
		if (e.id !== id) return e;
		return ShoppingCart.setName(e, name);
	});
}

export function setDescription(list: Type, id: string, name: string): Type {
	return list.map((e) => {
		if (e.id !== id) return e;
		return ShoppingCart.setDescription(e, name);
	});
}

export function setProductQuantity(
	list: Type,
	id: string,
	input: {
		productId: string;
		quantity: number;
	}
): Type {
	return list.map((e) => {
		if (e.id !== id) return e;
		return ShoppingCart.setProductQuantity(e, input.productId, input.quantity);
	});
}

export async function setQuantity(
	list: Type,
	id: string,
	input: {
		itemId: string;
		quantity: number;
	}
): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.setQuantityAndFetch(i, input.itemId, input.quantity));
	}
	return output;
}

export async function save(list: Type, id: string): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.save(i));
	}
	return output;
}

export function addNewCart(list: Type): Type {
	const hasDefaultCart: boolean = hasDefault(list);
	const newCart = list.find((e) => e.id === '');
	if (newCart) return list;
	return [ShoppingCart.create(!hasDefaultCart), ...list];
}

export async function removeAppearance(
	list: Type,
	cartId: string,
	appearanceId: string
): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== cartId) {
			output.push(i);
			continue;
		}
		output.push(await ShoppingCart.removeAppearance(i, appearanceId));
	}
	return output;
}

export async function initializeSingleCart(list: Type, id: string): Promise<Type> {
	const output: Type = [];
	for (const i of list) {
		if (i.id !== id) {
			output.push(i);
			continue;
		}
		const cart = await Functor.From(i)
			.mapAsync(ShoppingCart.getDetailsFromApi)
			.mapAsync(ShoppingCart.enrichWithProductInformations)
			.get();
		output.push(cart);
	}
	return output;
}
